import React, { useCallback, useEffect, useState } from 'react';
import { X } from 'lucide-react';
import BrandLoader from 'shared/components/ui/BrandLoader';
import { timeAgo, formatDate } from 'features/agents/lib/format';
import { activityNeutralButtonClass, drawerIconButtonClass } from 'features/activity/styles/dataTableStyles';
import VersionList from 'shared/components/ui/VersionList';
import { useAppDialog } from 'shared/context/AppDialogContext';
import {
  listWorkflowVersions,
  republishWorkflowVersion,
  restoreWorkflowVersion,
} from 'features/agents/api/agentsApi';

const PAGE = 20;
const MAX = 50;

export default function VersionHistoryPanel({
  workflowId,
  publishedVersionId,
  hasUnpublishedChanges,
  onClose,
  onRepublished,
  onRestored,
  onPreviewVersion,
  previewVersionId,
}) {
  const [versions, setVersions] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [limit, setLimit] = useState(PAGE);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actingId, setActingId] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  const { confirm: confirmDialog } = useAppDialog();
  const [, tick] = useState(0);

  useEffect(() => {
    const id = setInterval(() => tick((n) => n + 1), 60_000);
    return () => clearInterval(id);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await listWorkflowVersions(workflowId, { limit });
      setVersions(data.versions || []);
      setTotalCount(data.total ?? (data.versions || []).length);
    } catch (e) {
      setError(e?.response?.data?.error || e.message || 'Failed to load versions');
    } finally {
      setLoading(false);
    }
  }, [workflowId, limit]);

  useEffect(() => {
    load();
  }, [load]);

  const handleRepublish = async (versionId) => {
    const ok = await confirmDialog('This version becomes the published one and runs from now on. The editor switches to it too.', {
      title: 'Publish this version',
      confirmLabel: 'Publish',
    });
    if (!ok) return;
    setActingId(versionId);
    try {
      await republishWorkflowVersion(workflowId, versionId);
      await load();
      onRepublished?.();
    } catch (e) {
      setError(e?.response?.data?.error || e.message || 'Republish failed');
    } finally {
      setActingId(null);
    }
  };

  const handleRestore = async (versionId) => {
    const ok = await confirmDialog('The draft in the editor is replaced by this version. The published version does not change.', {
      title: 'Restore as draft',
      confirmLabel: 'Restore',
      danger: true,
    });
    if (!ok) return;
    setActingId(versionId);
    try {
      const { workflow, version } = await restoreWorkflowVersion(workflowId, versionId);
      onRestored?.(workflow, version);
    } catch (e) {
      setError(e?.response?.data?.error || e.message || 'Restore failed');
    } finally {
      setActingId(null);
    }
  };

  const items = versions.map((v) => {
    const isLive = v.is_live || v.id === publishedVersionId;
    const busy = actingId === v.id;
    return {
      id: v.id,
      label: `v${v.version_number}`,
      live: isLive,
      active: previewVersionId === v.id,
      time: <span title={formatDate(v.published_at) || undefined}>{timeAgo(v.published_at)}</span>,
      author: v.published_by_name || '',
      // A note, how big it is, and whether an earlier version is the same graph.
      note: [
        v.note || v.publish_note || v.change_note || '',
        v.node_count != null ? `${v.node_count} ${v.node_count === 1 ? 'step' : 'steps'}` : '',
        v.same_graph_as ? `same as v${v.same_graph_as}` : '',
      ].filter(Boolean).join(' · '),
      meta: v.version_hash ? (
        <button
          type="button"
          title={`Copy version hash\n${v.version_hash}`}
          onClick={() => { try { navigator.clipboard.writeText(v.version_hash); setCopiedId(v.id); setTimeout(() => setCopiedId(null), 1200); } catch (_) { /* denied */ } }}
          className="shrink-0 font-mono text-[11px] text-[color:var(--text-muted)] hover:text-[color:var(--text-primary)]"
        >
          {copiedId === v.id ? 'copied' : v.version_hash.slice(0, 8)}
        </button>
      ) : null,
      actions: [
        { key: 'view', label: 'View', disabled: busy, onClick: () => onPreviewVersion?.(v) },
        { key: 'restore', label: 'Restore as draft', disabled: busy, busy, onClick: () => handleRestore(v.id) },
        !isLive && { key: 'live', label: 'Publish this version', disabled: busy, onClick: () => handleRepublish(v.id) },
      ],
    };
  });
  const rows = hasUnpublishedChanges
    ? [{ id: '__draft', label: 'Draft', note: 'Unpublished changes', actions: [] }, ...items]
    : items;
  const canLoadMore = !loading && totalCount > versions.length && limit < MAX;

  return (
    <aside
      aria-label="Versions"
      className="absolute top-0 right-0 bottom-0 w-[320px] max-w-full z-20 flex flex-col border-l border-[color:var(--border-default)] bg-[color:var(--surface-base)] shadow-token-lg"
      style={{ fontFamily: 'Inter, sans-serif' }}
    >
      <div className="shrink-0 flex h-12 items-center justify-between pl-4 pr-3">
        <h2 className="m-0 text-[13px] font-medium text-[color:var(--text-primary)]">Versions</h2>
        <button type="button" onClick={onClose} className={drawerIconButtonClass} aria-label="Close" title="Close">
          <X size={16} aria-hidden />
        </button>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto border-t border-[color:var(--border-subtle)]">
        {loading && versions.length === 0 && (
          <div className="flex items-center justify-center py-8"><BrandLoader size="sm" label="Loading versions" /></div>
        )}
        {error && <p className="px-4 py-2 text-[12px] text-danger" role="alert">{error}</p>}
        {(versions.length > 0 || (!loading && !error)) && (
          <VersionList
            empty="No published versions yet. Publish to create v1."
            liveLabel="Published"
            items={rows}
          />
        )}
        {canLoadMore && (
          <div className="flex justify-center py-3">
            <button type="button" onClick={() => setLimit((l) => Math.min(MAX, l + PAGE))} className={activityNeutralButtonClass}>Load more</button>
          </div>
        )}
      </div>
    </aside>
  );
}
